import React from 'react';
import {
  Autocomplete,
  Badge,
  Box,
  Button,
  Chip,
  Collapse,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { Clear as ClearIcon, ExpandLess, ExpandMore, FilterList, Search as SearchIcon } from '@mui/icons-material';
import { Customer, PaymentStatus } from '../../types';
import { useLanguage } from '../../contexts/LanguageContext';
import TableFilterPanel from '../common/TableFilterPanel';

interface SalesHistoryFiltersProps {
  saleNumber: string;
  paymentStatus: PaymentStatus | '';
  customers: Customer[];
  selectedCustomer: Customer | null;
  customerInputValue: string;
  onSaleNumberChange: (value: string) => void;
  onCustomerSelect: (customer: Customer | null) => void;
  onCustomerInputChange: (value: string) => void;
  onPaymentStatusChange: (value: PaymentStatus | '') => void;
  onSearch: () => void;
  onClear: () => void;
  onRemoveAppliedFilter: (field: 'saleNumber' | 'paymentStatus') => void;
  appliedSaleNumber?: string;
  appliedPaymentStatus?: PaymentStatus;
  appliedFilterCount: number;
}

const SalesHistoryFilters: React.FC<SalesHistoryFiltersProps> = ({
  saleNumber,
  paymentStatus,
  customers,
  selectedCustomer,
  customerInputValue,
  onSaleNumberChange,
  onCustomerSelect,
  onCustomerInputChange,
  onPaymentStatusChange,
  onSearch,
  onClear,
  onRemoveAppliedFilter,
  appliedSaleNumber,
  appliedPaymentStatus,
  appliedFilterCount,
}) => {
  const { t } = useLanguage();
  const theme = useTheme();
  const isCompact = useMediaQuery(theme.breakpoints.down('md'));
  const [showFilters, setShowFilters] = React.useState(false);

  return (
    <>
      {!isCompact && <Box sx={{ p: 2.5 }}>
        <Stack direction="row" spacing={1.25} alignItems="center">
          <Autocomplete
            sx={{ flex: 1, minWidth: 0 }}
            options={customers}
            getOptionLabel={option => `${option.firstName} ${option.lastName}`}
            value={selectedCustomer}
            onChange={(_event, newValue) => onCustomerSelect(newValue)}
            inputValue={customerInputValue}
            onInputChange={(_event, newInputValue) => onCustomerInputChange(newInputValue)}
            renderInput={params => <TextField {...params} placeholder={t('sales.typeCustomerName')} inputProps={{ ...params.inputProps, 'aria-label': t('sales.searchByCustomer'), 'data-testid': 'sales-history-customer-search' }} InputProps={{ ...params.InputProps, startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment> }} />}
            renderOption={(props, option) => {
              const { key, ...optionProps } = props;
              return <Box component="li" key={key} {...optionProps}><Box><Typography>{option.firstName} {option.lastName}</Typography>{option.phone && <Typography variant="body2" color="text.secondary">{option.phone}</Typography>}{option.email && <Typography variant="body2" color="text.secondary">{option.email}</Typography>}</Box></Box>;
            }}
            noOptionsText={customerInputValue.length < 2 ? t('sales.typeAtLeastTwoCharacters') : t('sales.noCustomersFound')}
          />
          <Badge badgeContent={appliedFilterCount} color="warning" invisible={appliedFilterCount === 0}>
            <Button variant="outlined" startIcon={<FilterList />} endIcon={showFilters ? <ExpandLess /> : <ExpandMore />} aria-expanded={showFilters} aria-controls="sales-history-filters" onClick={() => setShowFilters(value => !value)}>{t('common.filters')}</Button>
          </Badge>
        </Stack>
        <TableFilterPanel id="sales-history-filters" open={showFilters} columns={2} onClear={onClear} onApply={() => { onSearch(); setShowFilters(false); }}
          activeFilters={[
            appliedSaleNumber ? { key: 'saleNumber', label: `${t('sales.saleNumber')}: ${appliedSaleNumber}`, onDelete: () => onRemoveAppliedFilter('saleNumber') } : null,
            appliedPaymentStatus ? { key: 'paymentStatus', label: `${t('sales.paymentStatus')}: ${appliedPaymentStatus === 'PAID' ? t('sales.paid') : appliedPaymentStatus === 'PARTIAL' ? t('sales.partiallyPaid') : t('sales.unpaid')}`, onDelete: () => onRemoveAppliedFilter('paymentStatus') } : null,
          ].filter((filter): filter is { key: string; label: string; onDelete: () => void } => Boolean(filter))}
          fields={<>
              <TextField label={t('sales.searchBySaleNumber')} value={saleNumber} onChange={event => onSaleNumberChange(event.target.value)} placeholder={t('sales.enterSaleNumber')} sx={{ flex: 1 }} />
              <FormControl><InputLabel>{t('sales.paymentStatus')}</InputLabel><Select value={paymentStatus} onChange={event => onPaymentStatusChange(event.target.value as PaymentStatus | '')} label={t('sales.paymentStatus')}><MenuItem value="">{t('sales.all')}</MenuItem><MenuItem value="PAID">{t('sales.paid')}</MenuItem><MenuItem value="PARTIAL">{t('sales.partiallyPaid')}</MenuItem><MenuItem value="UNPAID">{t('sales.unpaid')}</MenuItem></Select></FormControl>
          </>}
        />
      </Box>}

      {isCompact && <>
        <Box component="form" onSubmit={event => { event.preventDefault(); onSearch(); }} sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1.5, pt: 1.5 }}>
          <Autocomplete
            sx={{ flex: 1, minWidth: 0, '& .MuiOutlinedInput-root': { height: 44, borderRadius: 2, bgcolor: 'action.hover', '& fieldset, &:hover fieldset': { border: 0 }, '&.Mui-focused fieldset': { border: '2px solid', borderColor: 'primary.main' } } }}
            options={customers}
            getOptionLabel={option => `${option.firstName} ${option.lastName}`}
            value={selectedCustomer}
            onChange={(_event, newValue) => onCustomerSelect(newValue)}
            inputValue={customerInputValue}
            onInputChange={(_event, newInputValue) => onCustomerInputChange(newInputValue)}
            renderInput={params => (
              <TextField
                {...params}
                placeholder={t('sales.searchCustomerPlaceholder')}
                inputProps={{ ...params.inputProps, 'aria-label': t('sales.searchByCustomer'), 'data-testid': 'sales-history-customer-search' }}
                InputProps={{ ...params.InputProps, startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment> }}
              />
            )}
            renderOption={(props, option) => {
              const { key, ...optionProps } = props;
              return <Box component="li" key={key} {...optionProps}><Box><Typography variant="body1">{option.firstName} {option.lastName}</Typography>{option.phone && <Typography variant="body2" color="text.secondary">{option.phone}</Typography>}</Box></Box>;
            }}
            noOptionsText={customerInputValue.length < 2 ? t('sales.typeAtLeastTwoCharacters') : t('sales.noCustomersFound')}
          />
          <Badge
            badgeContent={appliedFilterCount}
            color="warning"
            invisible={appliedFilterCount === 0}
            overlap="circular"
            sx={{ flexShrink: 0, '& .MuiBadge-badge': { fontWeight: 700, border: '2px solid', borderColor: 'background.paper' } }}
          >
            <IconButton aria-label={`${t('common.filters')}${appliedFilterCount ? ` (${appliedFilterCount})` : ''}`} aria-expanded={showFilters} aria-controls="sales-history-filters" onClick={() => setShowFilters(value => !value)} sx={{ width: 44, height: 44, flexShrink: 0, color: 'primary.main' }}>
              <FilterList />
            </IconButton>
          </Badge>
        </Box>
      </>}

      {isCompact && appliedFilterCount > 0 && <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', px: 1.5, py: 1 }}>
        <Typography variant="caption" color="text.secondary">{t('debts.activeFilters')}</Typography>
        {appliedSaleNumber && <Chip size="small" label={`${t('sales.saleNumber')}: ${appliedSaleNumber}`} onDelete={() => onRemoveAppliedFilter('saleNumber')} sx={{ bgcolor: 'action.selected' }} />}
        {appliedPaymentStatus && <Chip
          size="small"
          label={`${t('sales.paymentStatus')}: ${appliedPaymentStatus === 'PAID' ? t('sales.paid') : appliedPaymentStatus === 'PARTIAL' ? t('sales.partiallyPaid') : t('sales.unpaid')}`}
          onDelete={() => onRemoveAppliedFilter('paymentStatus')}
          sx={{ bgcolor: 'action.selected' }}
        />}
      </Box>}

      {isCompact && <>
        <Collapse in={showFilters}>
          <Box id="sales-history-filters" sx={{ mx: 1.5, mt: 1.5, p: 1.5, bgcolor: 'action.hover', border: '1px solid', borderColor: 'divider', borderRadius: 1.5 }}>
            <Stack spacing={1.5} component="form" onSubmit={event => { event.preventDefault(); onSearch(); setShowFilters(false); }}>
              <TextField label={t('sales.searchBySaleNumber')} value={saleNumber} onChange={event => onSaleNumberChange(event.target.value)} placeholder={t('sales.enterSaleNumber')} size="small" fullWidth />
              <FormControl fullWidth size="small">
                <InputLabel>{t('sales.paymentStatus')}</InputLabel>
                <Select value={paymentStatus} onChange={event => onPaymentStatusChange(event.target.value as PaymentStatus | '')} label={t('sales.paymentStatus')}>
                  <MenuItem value="">{t('sales.all')}</MenuItem>
                  <MenuItem value="PAID">{t('sales.paid')}</MenuItem>
                  <MenuItem value="PARTIAL">{t('sales.partiallyPaid')}</MenuItem>
                  <MenuItem value="UNPAID">{t('sales.unpaid')}</MenuItem>
                </Select>
              </FormControl>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
                <Button variant="text" startIcon={<ClearIcon />} onClick={() => { onClear(); setShowFilters(false); }} sx={{ minHeight: 44 }}>{t('common.clearFilters')}</Button>
                <Button variant="contained" startIcon={<SearchIcon />} type="submit" sx={{ minHeight: 44 }}>{t('common.search')}</Button>
              </Box>
            </Stack>
          </Box>
        </Collapse>
      </>}
    </>
  );
};

export default SalesHistoryFilters;
